import { useState } from "react";
import { toast } from "sonner";
import { Download, FileSignature, Loader2, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { formatApiErrorDetail } from "@/lib/api";
import { eur } from "@/lib/format";
import { DocumentoContrato } from "@/components/contratos/DocumentoContrato";
import { FirmaClienteForm } from "@/components/contratos/FirmaClienteForm";
import { fechaCorta } from "@/components/contratos/constants";
import { EstadoContrato } from "@/components/contratos/EstadoContrato";

// Contratos del portal: los pendientes se revisan y firman aquí mismo.
export const ContratosPortal = ({ contratos, api, onFirmado }) => {
  const [vista, setVista] = useState(null);
  const [cargando, setCargando] = useState("");
  const abrir = async (c) => {
    setCargando(c.id);
    try { const { data } = await api.http.get(`/contratos/${c.id}`); setVista({ id: c.id, v: data }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCargando(""); }
  };
  const firmar = async (payload) => {
    try { await api.http.post(`/contratos/${vista.id}/firmar`, payload); toast.success("Contrato firmado. Te hemos enviado una copia por email."); setVista(null); onFirmado(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const pdf = (c) => api.descargar(`/contratos/${c.id}/pdf`, `contrato_${c.numero}.pdf`).catch(() => toast.error("No se pudo descargar el PDF"));
  return (
    <>
      <ul className="space-y-2.5">
        {contratos.map((c) => (
          <li key={c.id} className="rounded-2xl bg-white border border-slate-200 shadow-sm px-4 sm:px-5 py-3.5 flex flex-wrap items-center gap-3" data-testid={`contrato-portal-${c.id}`}>
            <FileSignature className="w-5 h-5 text-[#00B4CC] shrink-0" />
            <div className="flex-1 min-w-[160px]"><p className="font-bold text-[#1B2B4B]">{c.titulo}</p>
              <p className="text-xs text-slate-500">{c.numero} · {eur(c.precio_total)} · {c.estado === "firmado" ? `firmado el ${fechaCorta(c.firmado_at)}` : `enviado el ${fechaCorta(c.enviado_at)}`}</p></div>
            <EstadoContrato estado={c.estado} testid={`contrato-portal-estado-${c.id}`} />
            {c.estado === "firmado"
              ? <Button variant="outline" size="sm" onClick={() => pdf(c)} data-testid={`contrato-portal-pdf-${c.id}`}><Download className="w-4 h-4 mr-1" /> PDF</Button>
              : <Button size="sm" onClick={() => abrir(c)} disabled={!!cargando} className="bg-[#1B2B4B] hover:bg-[#13203A] text-white" data-testid={`contrato-portal-firmar-${c.id}`}>
                {cargando === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PenLine className="w-4 h-4 mr-1" /> Revisar y firmar</>}</Button>}
          </li>
        ))}
      </ul>
      <Dialog open={!!vista} onOpenChange={(v) => { if (!v) setVista(null); }}>
        <DialogContent className="max-w-4xl w-[calc(100vw-1rem)] max-h-[94vh] overflow-y-auto p-2 sm:p-4 bg-[#F4F7FA] rounded-2xl" data-testid="contrato-portal-dialog">
          <DialogTitle className="sr-only">Contrato</DialogTitle>
          <DialogDescription className="sr-only">Revisa el contrato y fírmalo al final.</DialogDescription>
          {vista && <div className="space-y-4"><DocumentoContrato v={vista.v} /><FirmaClienteForm v={vista.v} onFirmar={firmar} /></div>}
        </DialogContent>
      </Dialog>
    </>
  );
};
